import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';

import { INITIAL_CONTEXT, SettingsContext } from './constants';
import {
  ESettingsAliases,
  IGetSettingsResponse,
  ISetSettingRequest,
  ISetSettingResponse,
  ISetSettingsRequest,
  ISetSettingsResponse,
  ISetting,
  ISettingsContext,
  TSetSetting,
  TSetSettings,
} from './interfaces';
import {
  GET_SETTINGS_VALUE,
  UPDATE_MULTIPLE_SETTING_VALUE,
  UPDATE_SETTING_VALUE,
} from './schema';
import { getNotificationData, getSettingsValues } from './utils';

export const SettingsProvider: FC = ({ children }) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { isAuthInitialized, tokens, permissions },
  } = useContext(AuthContext);

  const [state, setState] = useState<ISettingsContext['state']>(
    INITIAL_CONTEXT.state
  );

  const [disabledSettings, setDisabledSettings] = useState<
    Set<ESettingsAliases>
  >(new Set());
  const settingValuesRef = useRef<Array<ISetting>>([]);

  const [loadSettings, { loading, data: settings }] =
    useLazyQuery<IGetSettingsResponse>(GET_SETTINGS_VALUE, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (
      isAuthInitialized &&
      tokens?.access_token &&
      permissions?.has('setting_browse')
    ) {
      loadSettings().finally(() => {
        setState((prevState) => ({
          ...prevState,
          isInitialized: true,
        }));
      });
    }
  }, [isAuthInitialized, tokens?.access_token, permissions]);

  useEffect(() => {
    if (!settings) return;
    settingValuesRef.current = settings.settingValues ?? [];
    if (settings.settingValues?.length) {
      const settingsDisabled = new Set<ESettingsAliases>();

      settings.settingValues.forEach(({ alias, disabled }) => {
        if (disabled) {
          settingsDisabled.add(alias);
        }
      });

      setDisabledSettings(settingsDisabled);
    }

    setState((prevState) => ({
      ...prevState,
      isInitialized: true,
      settings: getSettingsValues(
        settings?.settingValues ?? [],
        settings.currencies
      ),
      currencies: settings.currencies,
    }));
  }, [settings]);

  const applyUpdatedSettings = useCallback(
    (updatedSettings: Array<Omit<ISetting, 'disabled'>>) => {
      const updatesByAlias = new Map(
        updatedSettings.map((setting) => [setting.alias, setting])
      );
      const existingAliases = new Set(
        settingValuesRef.current.map((setting) => setting.alias)
      );
      const nextSettingValues = [
        ...settingValuesRef.current.map((setting) => {
          const updated = updatesByAlias.get(setting.alias);
          return updated ? { ...setting, ...updated } : setting;
        }),
        ...updatedSettings
          .filter((setting) => !existingAliases.has(setting.alias))
          .map((setting) => ({ ...setting, disabled: false })),
      ];

      settingValuesRef.current = nextSettingValues;
      setState((prevState) => ({
        ...prevState,
        settings: getSettingsValues(nextSettingValues, prevState.currencies),
      }));
    },
    []
  );

  const [updateSettingMutation, { loading: isLoadingUpdateSetting }] =
    useMutation<ISetSettingResponse, ISetSettingRequest>(UPDATE_SETTING_VALUE, {
      onCompleted: (data) => {
        if (!data || !data.setSetting) return;
        applyUpdatedSettings([data.setSetting]);
        notification.info(
          getNotificationData(data.setSetting, t, data.setSetting.alias)
        );
      },
      onError: () => {
        notification.info({
          t,
          type: 'error',
          title: t('errors.updated.title'),
          message: t('setting.updated.errors.message'),
          dismiss: 5,
        });
      },
      update: (cache, result) => {
        if (!result || !result.data?.setSetting) return;

        const updatedSetting = result.data.setSetting;

        cache.updateQuery<IGetSettingsResponse>(
          { query: GET_SETTINGS_VALUE },
          (current) => {
            if (!current) return current;
            const exists = current.settingValues.some(
              (setting) => setting.alias === updatedSetting.alias
            );
            return {
              ...current,
              settingValues: exists
                ? current.settingValues.map((setting) =>
                    setting.alias === updatedSetting.alias
                      ? { ...setting, ...updatedSetting }
                      : setting
                  )
                : [
                    ...current.settingValues,
                    { ...updatedSetting, disabled: false },
                  ],
            };
          }
        );
      },
    });

  const [updateMultipleSettingMutation, { loading: isLoadingUpdateSettings }] =
    useMutation<ISetSettingsResponse, ISetSettingsRequest>(
      UPDATE_MULTIPLE_SETTING_VALUE,
      {
        onCompleted: (data) => {
          if (!data?.setSettings?.length) return;
          applyUpdatedSettings(data.setSettings);
        },
        onError: () => {
          notification.info({
            t,
            type: 'error',
            title: t('errors.updated.title'),
            message: t('setting.updated.errors.message'),
            dismiss: 5,
          });
        },

        update: (cache, result) => {
          if (!result?.data?.setSettings?.length) return;

          const updatedSettings = result.data.setSettings;
          const updatesByAlias = new Map(
            updatedSettings.map((setting) => [setting.alias, setting])
          );
          cache.updateQuery<IGetSettingsResponse>(
            { query: GET_SETTINGS_VALUE },
            (current) => {
              if (!current) return current;
              const existingAliases = new Set(
                current.settingValues.map((setting) => setting.alias)
              );
              return {
                ...current,
                settingValues: [
                  ...current.settingValues.map((setting) => {
                    const updated = updatesByAlias.get(setting.alias);
                    return updated ? { ...setting, ...updated } : setting;
                  }),
                  ...updatedSettings
                    .filter((setting) => !existingAliases.has(setting.alias))
                    .map((setting) => ({ ...setting, disabled: false })),
                ],
              };
            }
          );
        },
      }
    );

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isLoading: loading || isLoadingUpdateSetting || isLoadingUpdateSettings,
    }));
  }, [loading, isLoadingUpdateSetting, isLoadingUpdateSettings]);

  const setSetting: TSetSetting = useCallback(
    ({ alias, value }) =>
      updateSettingMutation({
        variables: {
          input: {
            alias,
            value,
          },
        },
      }),
    [updateSettingMutation, t]
  );

  const setSettings: TSetSettings = useCallback(
    (settings) =>
      updateMultipleSettingMutation({
        variables: {
          input: {
            list: settings,
          },
        },
      }),
    [updateMultipleSettingMutation, t]
  );

  return (
    <SettingsContext.Provider
      value={{
        state,
        actions: { setSetting, loadSettings, setSettings },
        disable: disabledSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
